import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import './styles/z-index-tokens.css';

import App from './App.tsx';
import { ErrorBoundaryEnhanced } from '@/components/ErrorBoundaryEnhanced';
// Sentry is loaded lazily (see below) to keep it off the critical path.
import { registerServiceWorker } from '@/lib/registerServiceWorker';
import { killAllServiceWorkers } from '@/lib/swKiller';

import { initPromise as i18nInitPromise } from './lib/i18n';
import('@/utils/chunkLoadErrorHandler').catch(() => {});

const isLovablePreview = 
  location.hostname.endsWith('lovableproject.com') || 
  location.hostname.endsWith('lovable.app') ||
  location.hostname.endsWith('lovable.dev');

const rootEl = document.getElementById('root');

/** Force-hide the native splash screen. Safe to call multiple times or on web. */
async function hideSplashScreen() {
  try {
    const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
    if (!isNative) return;
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (_) {
    // Splash plugin not available — ignore
  }
}

if (rootEl) {
  const isCapacitorNative = !!(window as any).Capacitor?.isNativePlatform?.();
  const shouldKillSW = isLovablePreview || import.meta.env.DEV || isCapacitorNative;

  // Safety net: force-hide splash after 5s no matter what
  if (isCapacitorNative) {
    setTimeout(hideSplashScreen, 5000);
  }

  const showBootFallback = () => {
    hideSplashScreen();
    if (rootEl && !rootEl.hasChildNodes()) {
      rootEl.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui">
          <div style="text-align:center">
            <p style="font-size:16px;color:#333">Erreur de chargement</p>
            <button onclick="location.reload()" style="margin-top:12px;padding:8px 16px;cursor:pointer;border:1px solid #ccc;border-radius:6px;background:#fff">
              Recharger
            </button>
          </div>
        </div>`;
    }
  };

  const bootTimeout = setTimeout(showBootFallback, 10000);

  (async () => {
    if (shouldKillSW) {
      await killAllServiceWorkers(true);
    }

    try {
      await i18nInitPromise;
    } catch (e) {
      if (import.meta.env.DEV) console.error('[Boot] i18n init failed, continuing with defaults:', e);
    }

    // Defer Sentry init to after hydration — removes ~150-300ms parse cost from critical path
    const scheduleSentry = (cb: () => void) => {
      if (typeof (window as any).requestIdleCallback === 'function') {
        (window as any).requestIdleCallback(cb, { timeout: 3000 });
      } else {
        setTimeout(cb, 1500);
      }
    };
    scheduleSentry(() => {
      import('@/lib/sentry').then(({ initSentry }) => initSentry()).catch(() => {});
    });
    const isCapacitorNative = !!(window as any).Capacitor?.isNativePlatform?.();
    if (import.meta.env.PROD && !isLovablePreview && !isCapacitorNative) {
      registerServiceWorker();
    }

    if (isCapacitorNative) {
      import('@/lib/capacitor').then(({ initNativePlugins, setupDeepLinks }) => {
        initNativePlugins();
        setupDeepLinks((path) => {
          window.location.href = path;
        });
      }).catch((e) => {
        if (import.meta.env.DEV) console.error('[Boot] Capacitor init failed:', e);
        hideSplashScreen();
      });
    }

    clearTimeout(bootTimeout);

    createRoot(rootEl).render(
      <React.StrictMode>
        <ErrorBoundaryEnhanced>
          <App />
        </ErrorBoundaryEnhanced>
      </React.StrictMode>
    );

    // Hide splash after React has rendered
    await hideSplashScreen();
  })().catch((err) => {
    if (import.meta.env.DEV) console.error('[Boot] Fatal error during initialization:', err);
    clearTimeout(bootTimeout);
    showBootFallback();
  });
}
